import { useState, useEffect, useCallback } from "react";
import { Repeat, ShieldCheck, Plus, X, Loader2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useFb } from "../../utils/football";
import PlayerPickerModal from "./PlayerPickerModal";

const MAX_SUBS = 3;

/**
 * PowerUpsPanel — consumables usage on a locked-but-not-final squad (before kickoff).
 * Super Sub adds a 12th bench player (1x scoring, never captain). Penalty Shield zeroes
 * out red cards / own goals / missed penalties for the match day. Remove/deactivate
 * before kickoff refunds +1.
 */
export default function PowerUpsPanel({ selectedDay, squad, teams, budgetRemaining = 100, consumablesUsed = {}, onChanged }) {
  const fb = useFb();
  const navigate = useNavigate();
  const [inv, setInv] = useState({ super_sub: 0, no_penalty_shield: 0 });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const loadInv = useCallback(async () => {
    try { setInv(await fb.get("/football/consumables/inventory")); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadInv(); }, [loadInv, squad, consumablesUsed]);

  const superSubs = squad.filter((s) => s.is_super_sub);
  const subCount = superSubs.length;
  const baseXI = squad.filter((s) => !s.is_super_sub).length;
  const shieldActive = !!consumablesUsed?.no_penalty_shield;
  const pickedIds = new Set(squad.map((s) => s.player_id));
  const canAddSub = baseXI >= 11 && inv.super_sub > 0 && subCount < MAX_SUBS;

  const addSub = async (player) => {
    setPickerOpen(false);
    setBusy("sub");
    try {
      await fb.post("/football/consumables/use/super-sub", { match_day: selectedDay, super_sub_player_id: player.id });
      toast.success(`${player.name} added as Super Sub`);
      await onChanged?.(); await loadInv();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add Super Sub"); }
    setBusy("");
  };

  const removeSub = async (slot) => {
    setBusy("sub");
    try {
      await fb.post("/football/consumables/remove/super-sub", { match_day: selectedDay, super_sub_player_id: slot.player_id });
      toast.success("Super Sub removed · refunded");
      await onChanged?.(); await loadInv();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove Super Sub"); }
    setBusy("");
  };

  const toggleShield = async () => {
    setBusy("shield");
    try {
      if (shieldActive) {
        await fb.post("/football/consumables/remove/penalty-shield", { match_day: selectedDay });
        toast.success("Penalty Shield deactivated · refunded");
      } else {
        if (inv.no_penalty_shield <= 0) { toast.error("No Penalty Shields available"); setBusy(""); return; }
        if (!window.confirm("Activate No Penalty Shield for this match day? It zeroes out red cards, own goals and missed penalties.")) { setBusy(""); return; }
        await fb.post("/football/consumables/use/penalty-shield", { match_day: selectedDay });
        toast.success("Penalty Shield activated");
      }
      await onChanged?.(); await loadInv();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  return (
    <div className="mt-4 rounded-2xl border border-amber-400/30 bg-card p-3.5" data-testid="powerups-panel">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-extrabold wc-gold">⚡ Power-Ups</h3>
        <button onClick={() => navigate("/subscribe")} className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300" data-testid="powerups-shop-link">
          <ShoppingBag size={12} /> Shop
        </button>
      </div>

      {/* Super Sub */}
      <div className="rounded-xl border border-border bg-background p-3" data-testid="powerups-supersub">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Repeat size={16} className="wc-gold" /><span className="text-sm font-bold">Super Sub</span></div>
          <span className="text-[11px] text-muted-foreground">{subCount}/{MAX_SUBS} used · own {inv.super_sub}</span>
        </div>
        {superSubs.length > 0 && (
          <div className="mt-2 space-y-1">
            {superSubs.map((s) => (
              <div key={s.player_id} className="flex items-center justify-between rounded-lg border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-xs">
                <span className="font-bold">{s.name} <span className="text-muted-foreground">({s.position})</span></span>
                <button onClick={() => removeSub(s)} disabled={busy === "sub"} className="text-red-400" data-testid={`remove-supersub-${s.player_id}`}><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => setPickerOpen(true)} disabled={!canAddSub || busy === "sub"} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg wc-bg-gold py-2 text-xs font-extrabold text-slate-900 disabled:opacity-40" data-testid="add-supersub-btn">
          {busy === "sub" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Super Sub
        </button>
        {inv.super_sub <= 0 && subCount === 0 && <p className="mt-1.5 text-center text-[11px] text-muted-foreground">No Super Subs — grab one from the Shop.</p>}
      </div>

      {/* Penalty Shield */}
      <div className="mt-2 rounded-xl border border-border bg-background p-3" data-testid="powerups-shield">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck size={16} className={shieldActive ? "text-emerald-400" : "wc-gold"} /><span className="text-sm font-bold">No Penalty Shield</span></div>
          <span className="text-[11px] text-muted-foreground">own {inv.no_penalty_shield}</span>
        </div>
        {shieldActive && <div className="mt-1.5 rounded-lg bg-emerald-400/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300" data-testid="shield-active-banner">🛡️ Shield active for this match day</div>}
        <button onClick={toggleShield} disabled={busy === "shield" || (!shieldActive && inv.no_penalty_shield <= 0)} className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-extrabold disabled:opacity-40 ${shieldActive ? "border border-red-400/40 bg-red-400/10 text-red-300" : "wc-bg-gold text-slate-900"}`} data-testid="toggle-shield-btn">
          {busy === "shield" ? <Loader2 size={14} className="animate-spin" /> : null} {shieldActive ? "Deactivate Shield" : "Activate Shield"}
        </button>
      </div>

      <PlayerPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        position={null}
        mode="add"
        pickedIds={pickedIds}
        budgetRemaining={budgetRemaining}
        teams={teams}
        onPick={addSub}
        onRemove={() => {}}
      />
    </div>
  );
}
